import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const CSSAspectRatioCalculator = () => {
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [width, setWidth] = useState('');
  const [result, setResult] = useState(null);
  const [chartData, setChartData] = useState(null);

  const aspectRatios = [
    { value: '16:9', label: '16:9 (Widescreen)' },
    { value: '4:3', label: '4:3 (Standard)' },
    { value: '3:2', label: '3:2 (Classic)' },
    { value: '1:1', label: '1:1 (Square)' },
    { value: '21:9', label: '21:9 (UltraWide)' },
    { value: '3:4', label: '3:4 (Vertical)' },
  ];

  const calculateAspectRatio = () => {
    if (!width) return;

    const [numerator, denominator] = aspectRatio.split(':').map(Number);
    const calculatedHeight = (width * denominator) / numerator;
    setResult(calculatedHeight.toFixed(2));

    // Prepare chart data
    const chartData = {
      labels: ['Selected Aspect Ratio', 'Your Dimensions'],
      datasets: [
        {
          label: 'Height',
          data: [width, calculatedHeight],
          backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(153, 102, 255, 0.6)'],
          borderColor: ['rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'],
          borderWidth: 1,
        },
      ],
    };

    setChartData(chartData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gray-100 p-4"
    >
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
        <Helmet>
          <title>CSS Aspect Ratio Calculator</title>
          <meta name="description" content="Calculate and visualize CSS aspect ratios for responsive design." />
        </Helmet>

        <div className="calculator-header mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">CSS Aspect Ratio Calculator</h1>
          <p className="text-gray-600">Calculate height based on selected aspect ratio and visualize the proportions.</p>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Aspect Ratio Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Aspect Ratio</label>
            <select
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value)}
              className="w-full p-3 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {aspectRatios.map((ratio) => (
                <option key={ratio.value} value={ratio.value}>
                  {ratio.label}
                </option>
              ))}
            </select>
          </div>

          {/* Dimensions Inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Width (px)</label>
              <input
                type="number"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                className="w-full p-3 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter width"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Calculated Height (px)</label>
              <input
                type="text"
                value={result || ''}
                readOnly
                className="w-full p-3 border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              />
            </div>
          </div>

          {/* Calculate Button */}
          <button
            onClick={calculateAspectRatio}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Calculate Aspect Ratio
          </button>

          {/* Chart Visualization */}
          {chartData && (
            <div className="mt-8 bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Visualization</h3>
              <div className="h-64">
                <Bar
                  data={chartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Pixels',
                        },
                      },
                      x: {
                        title: {
                          display: true,
                          text: 'Dimensions',
                        },
                      },
                    },
                    plugins: {
                      title: {
                        display: true,
                        text: 'Aspect Ratio Comparison',
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* How to Use Section */}
      <div className="max-w-3xl mx-auto mt-12 bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
        <div className="space-y-6 text-gray-600">
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Select Aspect Ratio</h3>
            <p>Choose the desired aspect ratio from the dropdown menu.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Enter Width</h3>
            <p>Input the width in pixels for your element.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Calculate</h3>
            <p>Click the "Calculate Aspect Ratio" button to get the corresponding height.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 4: Visualize</h3>
            <p>View the bar chart to visualize the aspect ratio proportions.</p>
          </div>
        </div>
      </div>

      {/* Example Calculation Section */}
      <div className="max-w-3xl mx-auto mt-12 bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
        <div className="text-gray-600">
          <p>Let's say you want to create a responsive video player with a 16:9 aspect ratio:</p>
          <ul className="list-disc pl-6 mt-2">
            <li>Select "16:9 (Widescreen)" from the dropdown</li>
            <li>Enter your desired width, for example 1200px</li>
            <li>Click "Calculate Aspect Ratio"</li>
            <li>The calculator will show the corresponding height (675px)</li>
            <li>The visualization will show the proportional relationship</li>
          </ul>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-3xl mx-auto mt-12 bg-white rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
        <div className="space-y-6 text-gray-600">
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">What is aspect ratio?</h3>
            <p>The proportional relationship between the width and height of an element.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">Why is aspect ratio important?</h3>
            <p>Maintaining proper aspect ratio ensures elements scale correctly across different screen sizes and devices.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">How do I implement this in CSS?</h3>
            <p>Use the calculated height value in your CSS for the corresponding element to maintain the aspect ratio.</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-2">What are common use cases?</h3>
            <p>Responsive images, videos, layouts, and any element that needs to maintain specific proportions.</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CSSAspectRatioCalculator;